import { CheckTemplateIntervalType } from "../../settings/checktemplates/types/CheckTypes.interfaces"
import {
  ServiceOrInspection,
  UpcomingService,
  UpcomingInspection,
  CheckType,
  IntervalDetail,
  isUpcomingService,
  isUpcomingInspection,
} from "./UpcomingChecksTab.interfaces"
import { t } from "i18next"

const extractBaseProperties = (item: any) => ({
  vehicleId: item.vehicleId,
  vehicleRegNumber: item.vehicleRegNumber,
  vehicleInternNumber: item.vehicleInternNumber,
  checkTemplateId: item.checkTemplateId,
  checkTemplateName: item.checkTemplateName,
  checkTemplateIntervalId: item.checkTemplateIntervalId,
  checkTemplateIntervalType: item.checkTemplateIntervalType,
  isOverdue: item.isOverdue,
  status: item.status,
  earliestDueDate: item.earliestDueDate,
  valueUntilService: item.valueUntilService,
  currentOdometerReading: item.currentOdometerReading,
  nextValue: item.nextValue,
  lastCompletedValue: item.lastCompletedValue,
})

const createIntervalDetail = (item: any): IntervalDetail => ({
  id: item.checkTemplateIntervalId,
  intervalType: item.checkTemplateIntervalType,
  intervalValue: item.intervalValue,
  nextValue: item.nextValue,
  lastCompletedValue: item.lastCompletedValue,
  status: item.status,
})

const getIntervalTypeDisplay = (intervalType: CheckTemplateIntervalType): string => {
  return intervalType === CheckTemplateIntervalType.Days ? t("global.date", "Date") : t("global.distance", "Distance")
}

export const createServiceEntry = (item: any): UpcomingService => {
  const service: UpcomingService = {
    ...extractBaseProperties(item),
    checkType: CheckType.Service,
    intervalType: getIntervalTypeDisplay(item.checkTemplateIntervalType),
    intervalValue: item.intervalValue,
    intervals: [createIntervalDetail(item)],
  }

  return service
}

export const createInspectionEntry = (item: any): UpcomingInspection => {
  const inspection: UpcomingInspection = {
    ...extractBaseProperties(item),
    checkType: CheckType.Inspection,
    hasInspectionBooking: item.hasInspectionBooking,
    inspectionBookingId: item.inspectionBookingId,
    inspectionBookingDate: item.inspectionBookingDate,
    inspectionBookingRemark: item.inspectionBookingRemark,
    vehicleCheckHistoryStatus: item.vehicleCheckHistoryStatus,
  }

  return inspection
}

const updateIntervalTypeDisplay = (service: UpcomingService): void => {
  if (!service.intervals) return

  const types = new Set<string>()
  service.intervals.forEach(interval => {
    types.add(getIntervalTypeDisplay(interval.intervalType))
  })
  service.intervalType = Array.from(types).sort().join(" & ")
}

export const mergeServiceItem = (existing: UpcomingService, item: any): void => {
  if (!existing.intervals) {
    existing.intervals = []
  }

  existing.intervals.push(createIntervalDetail(item))

  if (item.currentOdometerReading !== undefined && item.currentOdometerReading !== null) {
    existing.currentOdometerReading = item.currentOdometerReading
  }

  if (item.isOverdue) {
    existing.isOverdue = true
  }

  updateIntervalTypeDisplay(existing)
}

export const mergeInspectionItem = (existing: UpcomingInspection, item: any): void => {
  if (item.hasInspectionBooking) {
    existing.hasInspectionBooking = true
    existing.inspectionBookingId = item.inspectionBookingId
    existing.inspectionBookingDate = item.inspectionBookingDate
    existing.inspectionBookingRemark = item.inspectionBookingRemark
    existing.vehicleCheckHistoryStatus = item.vehicleCheckHistoryStatus
  }

  if (item.isOverdue) {
    existing.isOverdue = true
  }
}

export const updateStatus = (existing: ServiceOrInspection, newStatus: any): void => {
  if (newStatus === undefined || newStatus === null) return

  const currentStatus = typeof existing.status === "number"
    ? existing.status
    : parseInt(existing.status as string, 10) || 0

  const statusValue = typeof newStatus === "number"
    ? newStatus
    : parseInt(newStatus, 10) || 0

  existing.status = Math.max(currentStatus, statusValue)
}

export const processUpcomingChecks = (rawData: any[]): ServiceOrInspection[] => {
  const groupedMap = new Map<string, ServiceOrInspection>()

  rawData.forEach((item: any) => {
    const key = `${item.vehicleId}-${item.checkTemplateId}`
    const existing = groupedMap.get(key)

    if (existing) {
      if (isUpcomingService(existing) && item.checkType === CheckType.Service) {
        mergeServiceItem(existing, item)
      } else if (isUpcomingInspection(existing) && item.checkType === CheckType.Inspection) {
        mergeInspectionItem(existing, item)
      }

      updateStatus(existing, item.status)
    } else {
      if (item.checkType === CheckType.Service) {
        groupedMap.set(key, createServiceEntry(item))
      } else if (item.checkType === CheckType.Inspection) {
        groupedMap.set(key, createInspectionEntry(item))
      }
    }
  })

  return Array.from(groupedMap.values())
}

export const getDateInterval = (service: UpcomingService): IntervalDetail | undefined => {
  return service.intervals?.find(
    interval => interval.intervalType === CheckTemplateIntervalType.Days
  )
}

export const getDistanceInterval = (service: UpcomingService): IntervalDetail | undefined => {
  return service.intervals?.find(
    interval => interval.intervalType === CheckTemplateIntervalType.Distance
  )
}

export const getLastCompletedDateValue = (service: UpcomingService): string | undefined => {
  const dateInterval = getDateInterval(service)
  return dateInterval?.lastCompletedValue
}

export const getNextDateValue = (service: UpcomingService): string | undefined => {
  const dateInterval = getDateInterval(service)
  return dateInterval?.nextValue
}

export const getLastCompletedDistanceValue = (service: UpcomingService): string | undefined => {
  const distanceInterval = getDistanceInterval(service)
  return distanceInterval?.lastCompletedValue
}

export const getNextDistanceValue = (service: UpcomingService): string | undefined => {
  const distanceInterval = getDistanceInterval(service)
  return distanceInterval?.nextValue
}

export const calculateDaysUntilService = (service: UpcomingService): number | undefined => {
  const dateInterval = getDateInterval(service)
  if (!dateInterval?.nextValue) return undefined

  const nextDate = new Date(dateInterval.nextValue)
  const today = new Date()
  const diffTime = nextDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return diffDays
}

export const calculateDistanceUntilService = (service: UpcomingService): number | undefined => {
  const distanceInterval = getDistanceInterval(service)
  if (!distanceInterval?.nextValue || !service.currentOdometerReading) return undefined

  const nextDistance = parseFloat(distanceInterval.nextValue)
  const remaining = nextDistance - service.currentOdometerReading

  return remaining
}

const DAYS_TO_DISTANCE_RATIO = 250

const calculateUrgencyScore = (check: ServiceOrInspection): number => {
  if (isUpcomingInspection(check)) {
    const days = check.valueUntilService ?? Number.MAX_SAFE_INTEGER
    return days * DAYS_TO_DISTANCE_RATIO
  }

  if (isUpcomingService(check)) {
    const dateInterval = getDateInterval(check)
    const distanceInterval = getDistanceInterval(check)

    let dateScore = Number.MAX_SAFE_INTEGER
    let distanceScore = Number.MAX_SAFE_INTEGER

    if (dateInterval?.nextValue) {
      const days = calculateDaysUntilService(check)
      if (days !== undefined) {
        dateScore = days * DAYS_TO_DISTANCE_RATIO
      }
    }

    if (distanceInterval?.nextValue && check.currentOdometerReading) {
      const distance = calculateDistanceUntilService(check)
      if (distance !== undefined) {
        distanceScore = distance
      }
    }

    return Math.min(dateScore, distanceScore)
  }

  return Number.MAX_SAFE_INTEGER
}

// Sort checks by urgency (most urgent first)
// Uses normalized scoring where 1 day = 50 km
// Examples:
//   - 2 days (score: 100) comes before 105 km (score: 105)
//   - 97 km (score: 97) comes before 2 days (score: 100)
//   - Overdue items (negative scores) come first
export const sortByUrgency = (checks: ServiceOrInspection[]): ServiceOrInspection[] => {
  return [...checks].sort((a, b) => {
    const scoreA = calculateUrgencyScore(a)
    const scoreB = calculateUrgencyScore(b)

    return scoreA - scoreB
  })
}
